import { useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import {
  useSamplingConfirm,
  type SamplingMode,
} from "../../stores/samplingConfirm";
import { useFields } from "../../hooks/queries";
import { fieldTotalPrice } from "../../lib/samplePricing";
import { useSamplingPricePerAcre } from "../../hooks/useSamplingPrice";
import { OrderOptions } from "./OrderOptions";
import { useLabOrder } from "../../stores/labOrderStore";
import { orderOptionsProblem } from "../../lib/orderOptions";
import { Modal } from "../../ui/Modal";

// Sampling-order confirmation: shown before ANY soil sampling is ordered — a preset click the
// multi-field "run on all", the custom stepper, or an in-map point/zone editor.
/*
 *
 * Suppressing the frame is not suppressing the request: the host clears `hosted` when it closes so
 * the confirmation reappears here rather than being lost.
 */
export function SamplingConfirmDialog() {
  const pending = useSamplingConfirm((s) => s.pending);
  const hosted = useSamplingConfirm((s) => s.hosted);
  if (!pending || hosted) return null;
  return (
    <Modal>
      <div className="fixed inset-0 z-overlay flex items-center justify-center bg-black/40 p-4">
        <div
          className="flex w-full max-w-md flex-col rounded-lg bg-surface text-fg shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <SamplingConfirmBody framed />
        </div>
      </div>
    </Modal>
  );
}

/*
 *
 * The body — the field list, the prices, the total and the actions. ONE implementation, two frames
 * (this dialog and the animation window), because a second copy of a priced order confirmation is the
 * last thing that should be allowed to drift.
 */
export function SamplingConfirmBody({ framed = false }: { framed?: boolean }) {
  const pending = useSamplingConfirm((s) => s.pending);
  const clear = useSamplingConfirm((s) => s.clear);
  const { data: fields } = useFields();
  const [busy, setBusy] = useState<SamplingMode | "download" | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // the ORDER OPTIONS — live store values; an invalid phone blocks Save & Order only (Save-only and
  // Save & Download place no work order, so nothing about the crew's contact matters there).
  const notifyPhone = useLabOrder((s) => s.notifyPhone);
  const optionsProblem = orderOptionsProblem({ notifyPhone });
  const pricePerAcre = useSamplingPricePerAcre();

  // The dialog is mounted once at the App root and never unmounts, so its local state persists
  // across opens. Reset busy + error whenever the pending request changes (a new order, or cleared)
  // otherwise a prior success leaves `busy` set and the NEXT dialog opens permanently
  // disabled/undismissable.
  useEffect(() => {
    setBusy(null);
    setErr(null);
  }, [pending]);

  if (!pending) return null;

  const acresOf = (id: string) =>
    fields?.find((f) => f.field_id === id)?.acres ?? 0;
  const rows = pending.items.map((it) => {
    const acres = acresOf(it.field_id);
    return { ...it, price: fieldTotalPrice(acres, pricePerAcre) };
  });
  const nFields = rows.length;
  const total = rows.reduce((s, r) => s + (r.price ?? 0), 0);
  const anyPrice = rows.some((r) => r.price != null);

  async function go(mode: SamplingMode) {
    if (mode === "order" && optionsProblem) {
      setErr(optionsProblem);
      return;
    }
    setBusy(mode);
    setErr(null);
    try {
      await pending!.run(mode);
      clear();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Something went wrong.");
      setBusy(null);
    }
  }

  // Save & Download: persist exactly like Save, then download the zone file(s) through the same
  // smooth/clip/color chain as the map tab's Download Zones button.
  async function goDownload() {
    setBusy("download");
    setErr(null);
    try {
      await pending!.run("save");
      await pending!.download!();
      clear();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Something went wrong.");
      setBusy(null);
    }
  }

  return (
    <>
      {/* the header belongs to the standalone dialog; inside the animation the window already has one */}
      {framed && (
        <div className="flex items-center justify-between border-b border-line px-3 py-2">
          <h2 className="text-sm font-semibold">
            Order soil sampling{nFields > 1 ? ` · ${nFields} fields` : ""}
          </h2>
          <button
            onClick={clear}
            disabled={!!busy}
            className="rounded p-1 hover:bg-hover disabled:opacity-50"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
      )}
      <div className="flex flex-col gap-3 p-4 text-[12px]">
        <p className="text-muted">
          <span className="font-medium text-fg">Save</span> keeps the{" "}
          {rows.every((r) => r.kind === "zones") ? "zones" : "points"} on the
          map without ordering;{" "}
          <span className="font-medium text-fg">Save &amp; Order</span> also
          submits the sampling work order{nFields > 1 ? "s" : ""}.
          {pending.download ? (
            <>
              {" "}
              <span className="font-medium text-fg">
                Save &amp; Download
              </span>{" "}
              saves, then downloads the zone file{nFields > 1 ? "s" : ""} (no
              order).
            </>
          ) : null}
        </p>
        <ul className="max-h-56 divide-y divide-line overflow-auto rounded border border-line">
          {rows.map((r) => (
            <li
              key={r.field_id}
              className="flex items-baseline justify-between gap-2 px-2.5 py-1.5"
            >
              <span className="min-w-0 truncate text-fg">{r.field_name}</span>
              <span className="flex shrink-0 items-baseline gap-3 text-muted">
                <span className="tnum">
                  {r.count}{" "}
                  {r.kind === "points"
                    ? r.count === 1
                      ? "point"
                      : "points"
                    : r.count === 1
                      ? "zone"
                      : "zones"}
                </span>
                <span className="tnum w-16 text-right font-medium text-fg">
                  {r.price != null
                    ? `$${Math.round(r.price).toLocaleString()}`
                    : "—"}
                </span>
              </span>
            </li>
          ))}
        </ul>
        {anyPrice && (
          <div className="flex items-baseline justify-between border-t border-line pt-2 text-[13px]">
            <span className="font-semibold text-fg">
              Total{nFields > 1 ? ` · ${nFields} work orders` : ""}
            </span>
            <span className="tnum font-semibold text-fg">
              ${Math.round(total).toLocaleString()}
            </span>
          </div>
        )}
        {/* ORDER OPTIONS: depth · lab · package · additional config · notification phone saved on the work order(s) and shown on the Work Orders screen. */}
        <OrderOptions />
        {err && <p className="text-[11px] text-red-400">{err}</p>}
        <div className="flex items-center gap-2 pt-1">
          <button
            disabled={!!busy || !!optionsProblem}
            title={optionsProblem ?? undefined}
            onClick={() => void go("order")}
            className="flex items-center gap-1.5 rounded bg-accent px-3 py-1.5 font-medium text-canvas hover:bg-accent-hover disabled:opacity-50"
          >
            {busy === "order" ? (
              <Loader2 size={13} className="animate-spin" />
            ) : null}{" "}
            Save &amp; Order
          </button>
          <button
            disabled={!!busy}
            onClick={() => void go("save")}
            className="flex items-center gap-1.5 rounded border border-line px-3 py-1.5 text-fg hover:bg-hover disabled:opacity-50"
          >
            {busy === "save" ? (
              <Loader2 size={13} className="animate-spin" />
            ) : null}{" "}
            Save only
          </button>
          {pending.download && (
            <button
              disabled={!!busy}
              onClick={() => void goDownload()}
              className="flex items-center gap-1.5 rounded border border-line px-3 py-1.5 text-fg hover:bg-hover disabled:opacity-50"
            >
              {busy === "download" ? (
                <Loader2 size={13} className="animate-spin" />
              ) : null}{" "}
              Save &amp; Download
            </button>
          )}
          <button
            disabled={!!busy}
            onClick={clear}
            className="ml-auto text-[11px] text-muted hover:text-fg disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  );
}
